/**
 * Email Types for artha.link
 * Defines email-related types and enums for transactional emails
 */

import { EarningPotential } from "./creator";

/**
 * Email types supported by the system
 * Using const enum for compile-time optimization
 */
export const enum EmailType {
  WELCOME = "welcome",
  PASSWORD_RESET = "password_reset",
  PROFILE_APPROVED = "profile_approved",
  PROFILE_WAITLISTED = "profile_waitlisted",
  PROFILE_REJECTED = "profile_rejected",
  APPEAL_REQUESTED = "appeal_requested",
  APPEAL_APPROVED = "appeal_approved",
  APPEAL_REJECTED = "appeal_rejected",
  JOB_ALERT = "job_alert",
  DAILY_JOBS = "daily_rpa_jobs",
  WEEKLY_REVENUE_UPLOAD = "weekly_revenue_upload",
  WEEKLY_LEADERBOARD = "weekly_leaderboard",
  MONTHLY_LEADERBOARD = "monthly_leaderboard",
  WEEKLY_LEADERBOARD_WINNER = "weekly_leaderboard_winner",
  REPLIED_REMINDER = "replied_reminder_trigger",
  PAYOUT_SUCCESSFUL = "payout_successful",
}

/**
 * Email delivery status
 */
export const enum EmailDeliveryStatus {
  PENDING = "pending",
  SENT = "sent",
  DELIVERED = "delivered",
  FAILED = "failed",
  BOUNCED = "bounced",
}

/**
 * Email recipient information
 */
export interface EmailRecipient {
  email: string;
  userId: string;
  firstName?: string;
  lastName?: string;
  name?: string;
}

/**
 * Base email payload interface
 */
export interface BaseEmailPayload {
  type: EmailType;
  recipient: EmailRecipient;
}

/**
 * Welcome email data
 */
export interface WelcomeEmailData {
  signupDate: string;
}

/**
 * Password reset email data
 */
export interface PasswordResetEmailData {
  resetToken: string;
  resetUrl: string;
  expiresAt: string;
}

/**
 * Profile approved email data
 */
export interface ProfileApprovedEmailData {
  niches: string[];
  jobCount?: number;
}

/**
 * Profile waitlisted email data
 */
export interface ProfileWaitlistedEmailData {
  reason: string;
  niches: string[];
}

/**
 * Profile rejected email data
 */
export interface ProfileRejectedEmailData {
  reason: string;
  rejectionCode: string;
}

/**
 * Appeal requested email data
 */
export interface AppealRequestedEmailData {
  appealRequestedAt: string;
  originalRejectionReason?: string;
  name: string
}

/**
 * Appeal result email data
 */
export interface AppealResultEmailData {
  approved: boolean;
  reason?: string;
  processedAt: string;
}

/**
 * Job alert email data
 */
export interface JobAlertEmailData {
  jobs: Array<{
    title: string;
    company: string;
    location?: string;
    salary?: string;
    url: string;
  }>;
  totalMatches: number;
}

/**
 * Daily jobs email data
 */
export interface DailyJobsEmailData {
  jobs: Array<{
    title: string;
    company: string;
    location?: string;
    salary?: string;
    cpa?: number;
    url: string;
    description?: string;
  }>;
  totalJobCount: number;
  date: string;
  creatorName: string;
  profileUrl: string;
}

/**
 * Weekly revenue upload email data
 */
export interface WeeklyRevenueUploadEmailData {
  totalRevenue: number;
  totalClicks: number;
  eventCount: number;
  dateRange: string;
  creatorName: string;
}

/**
 * Leaderboard entry for emails
 */
export interface LeaderboardEmailEntry {
  rank: number;
  name: string;
  earnings: number | string;
  weeklyJobShareCount?: number;
}

/**
 * Weekly/Monthly leaderboard email data
 */
export interface LeaderboardEmailData {
  topCreators: LeaderboardEmailEntry[];
  rankings: LeaderboardEmailEntry[];
  currentUser: {
    rank: number;
    earnings: number | string;
  };
  creator_username: string;
  weeklyJobShareCount?: number;
  monthlyJobShareCount?: number;
}

/**
 * Weekly leaderboard winner email data (#1 only)
 */
export interface LeaderboardWinnerEmailData {
  earnings: number | string;
}

/**
 * Outreach nudge reminder email data
 */
export interface RepliedReminderEmailData {
  jobs: Array<{
    title: string;
    companyNm: string;
    location?: string;
    cpaValue?: number;
    url: string;
    companyLogo: string;
  }>;
  displayName: string | undefined;
  earningPotential: EarningPotential;
  profile_username: string; // To match template {{customer.profile_username}}
}

/**
 * Payout successful email data
 */
export interface PayoutSuccessfulEmailData {
  payout_amount: string | number;
}

/**
 * Union type for all email data payloads
 */
export type EmailData =
  | WelcomeEmailData
  | PasswordResetEmailData
  | ProfileApprovedEmailData
  | ProfileWaitlistedEmailData
  | ProfileRejectedEmailData
  | AppealRequestedEmailData
  | AppealResultEmailData
  | JobAlertEmailData
  | DailyJobsEmailData
  | WeeklyRevenueUploadEmailData
  | LeaderboardEmailData
  | LeaderboardWinnerEmailData
  | RepliedReminderEmailData
  | PayoutSuccessfulEmailData;

/**
 * Email send result
 */
export interface EmailSendResult {
  success: boolean;
  messageId?: string;
  error?: string;
  provider: string;
}

/**
 * Email provider configuration
 */
export interface EmailProviderConfig {
  name: string;
  enabled: boolean;
  priority: number;
}
